import { DBInstance } from '@repo/db'
import { ComponentInfoLoadChatsVO } from '@repo/types'
import React, { createContext, useCallback, useContext, useState } from 'react'

type UnreadsInfo = {
  lastUnreads: number
  currentUnreads: number
}

interface UnreadsContextProps {
  currentUnreads: number
  initUnreads: (currentUnreads: number, filters: ComponentInfoLoadChatsVO) => Promise<void>
  updateLocalUnreads: (filters: ComponentInfoLoadChatsVO) => Promise<void>
  updateUnreadsOnEvent: (filters: ComponentInfoLoadChatsVO) => Promise<void>
}

const UnreadsContext = createContext<UnreadsContextProps>({
  currentUnreads: 0,
  initUnreads: async () => undefined,
  updateLocalUnreads: async () => undefined,
  updateUnreadsOnEvent: async () => undefined
})

interface UnreadsProviderProps {
  dbInstance: DBInstance
  children?: React.ReactNode
}

export const UnreadsProvider = ({ children, dbInstance }: UnreadsProviderProps) => {
  const [unreadsInfo, setUnreadsInfo] = useState<UnreadsInfo>({
    lastUnreads: 0,
    currentUnreads: 0
  })

  const getLocalUnreads = useCallback(
    async (filters: ComponentInfoLoadChatsVO) => {
      const localUnreads = await dbInstance.chatRepository.getChatsUnreadsSum(filters)
      return localUnreads ?? 0
    },
    [dbInstance]
  )

  const updateLocalUnreads = useCallback(
    async (filters: ComponentInfoLoadChatsVO) => {
      const lastUnreads = await getLocalUnreads(filters)
      setUnreadsInfo((prevProps: UnreadsInfo) => ({ ...prevProps, lastUnreads }))
    },
    [getLocalUnreads]
  )

  const updateUnreadsOnEvent = useCallback(
    async (filters: ComponentInfoLoadChatsVO) => {
      const localUnreads = await getLocalUnreads(filters)
      setUnreadsInfo((prevState: UnreadsInfo) => ({
        currentUnreads: prevState.currentUnreads - (prevState.lastUnreads - localUnreads),
        lastUnreads: localUnreads
      }))
    },
    [getLocalUnreads]
  )

  const initUnreads = useCallback(
    async (currentUnreads: number, filters: ComponentInfoLoadChatsVO) => {
      const localUnreads = await getLocalUnreads(filters)
      setUnreadsInfo({
        lastUnreads: localUnreads,
        currentUnreads
      })
    },
    [getLocalUnreads]
  )

  return (
    <UnreadsContext.Provider
      value={{
        currentUnreads: unreadsInfo.currentUnreads,
        updateLocalUnreads,
        updateUnreadsOnEvent,
        initUnreads
      }}
    >
      {children}
    </UnreadsContext.Provider>
  )
}

export const useUnreads = () => useContext(UnreadsContext)
